import { request } from './request'
import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'

export type TraderDetailMainData = {
  traderCode?: string
  showName?: string
  shortNameLocal?: string
  annotation?: string
  webUrl?: string
  mobileUrl?: string
}

export type TraderDetailAssetStrengthTag = {
  label?: string
  value?: string
}

export type TraderDetailAssetStrengthTimeRangeOption = {
  label?: string
  value?: string
}

export type TraderDetailAssetStrengthChartSlice = {
  name?: string
  percent?: string
  amount?: string
  color?: string
  icon?: string
}

export type TraderDetailAssetStrengthPointChart = {
  labels?: string[]
  points?: Array<number | string>
  pointsVal?: number[]
}

export type TraderDetailAssetStrengthReserve = {
  title?: string
  score?: number
  scoreMax?: number
  amount?: string
  outperformPercentText?: string
  outperformPercentValue?: string
  chart?: {
    centerSymbol?: string
    centerAmount?: string
    slices?: TraderDetailAssetStrengthChartSlice[]
  }
}

export type TraderDetailAssetStrengthTrend = {
  title?: string
  score?: number
  scoreMax?: number
  amount?: string
  outperformPercentText?: string
  outperformPercentValue?: string
  changePercent?: string
  changeDirection?: string
  badge?: string
  chart?: TraderDetailAssetStrengthPointChart
}

export type TraderDetailAssetStrength = {
  title?: string
  summary?: {
    icon?: string
    symbol?: string
    price?: string
  }
  tags?: TraderDetailAssetStrengthTag[]
  timeRange?: TraderDetailAssetStrengthTimeRangeOption[] | { value?: string; options?: string[] }
  fundFlowTimeRange?: TraderDetailAssetStrengthTimeRangeOption[] | { value?: string; options?: string[] }
  tradingVolumeTimeRange?: TraderDetailAssetStrengthTimeRangeOption[] | { value?: string; options?: string[] }
  assetReserve?: TraderDetailAssetStrengthReserve
  fundFlow?: TraderDetailAssetStrengthTrend
  tradingVolume?: TraderDetailAssetStrengthTrend
}

export type TraderDetailBusinessRegulator = {
  icon?: string
  text?: string
}

export type TraderDetailBusinessPopup = {
  title?: string
  notes?: string
  riskWarning?: string
  tips?: {
    text?: string
    color?: string
  }
  regulators?: TraderDetailBusinessRegulator[]
  supportDocs?: string[]
}

export type TraderDetailLabel = {
  content?: string
  fontColor?: string
  backgroundColor?: string
  borderColor?: string
  text?: string
  type?: number
  labelType?: number
  businessPopup?: TraderDetailBusinessPopup
}

export type TraderDetailWebsite = {
  url?: string
  type?: number
  isLegal?: boolean
}

export type TraderDetailRatingCard = {
  type?: number
  title?: string
  value?: string
}

export type TraderDetailCompanyBasicData = {
  title?: string
  value?: string
  icon?: string
}

export type TraderDetailCompanyInvestment = {
  type?: number
  contactWay?: string
  icon?: string
}

export type TraderDetailSurveyLabel = {
  type?: number
  text?: string
  borderColor?: string
  backgroundColor?: string
  fontColor?: string
}

export type TraderDetailSurveyItem = {
  sid?: string
  cover?: string
  ico?: string
  title?: string
  summary?: string
  type?: number
  vrIcon?: string
  labels?: TraderDetailSurveyLabel[]
}

export type TraderDetailSurvey = {
  topicTitle?: string
  title?: string
  items?: TraderDetailSurveyItem[]
  flags?: string[]
}

export type TraderDetailGuaranteeItem = {
  title?: string
  content?: string
}

export type TraderDetailBitGuarantee = {
  topicTitle?: string
  exchangeGuaranteeAmount?: string
  platformText?: string
  iapCompensationStatus?: string
  items?: TraderDetailGuaranteeItem[]
}

export type TraderDetailResponse = {
  license?: {
    tabItems?: Array<Record<string, unknown>>
    [key: string]: unknown
  } | null
  assetStrength?: TraderDetailAssetStrength | null
  basicInfo?: {
    traderCode?: string
    showName?: string
    shortName?: string
    shortNameLocal?: string
    ico?: string
    annotation?: string
    website?: string
    mobileUrl?: string
    displayItems?: TraderDetailCompanyBasicData[]
  } | null
  traderData?: {
    basicData?: TraderDetailMainData
    allsites?: TraderDetailWebsite[]
    tianyanRatingCards?: TraderDetailRatingCard[]
    traderLabels?: TraderDetailLabel[] | null
    traderTopLabels?: TraderDetailLabel[] | null
    traderCompareLable?: TraderDetailLabel[] | null
    sortedLabels?: TraderDetailLabel[] | null
    labels?: TraderDetailLabel[] | null
    corporateProfiles?: unknown
  } | null
  companyInfo?: {
    basicData?: TraderDetailCompanyBasicData[]
    investments?: TraderDetailCompanyInvestment[]
    title?: string
  } | null
  bitGuarantee?: TraderDetailBitGuarantee | null
  safelyMeasuresList?: unknown
  assetReserves?: unknown
  survey?: TraderDetailSurvey | null
}

export type TraderDetailParams = {
  traderCode: string
  languageCode?: string
  countryCode?: string
  timeRangeType?: string | number
}

type TraderDetailRawResponse = TraderDetailResponse | { data?: TraderDetailResponse | null }

const hasWrappedTraderDetailData = (
  response: TraderDetailRawResponse,
): response is { data?: TraderDetailResponse | null } => Object.prototype.hasOwnProperty.call(response, 'data')

const normalizeTraderDetailResponse = (response: TraderDetailRawResponse | null): TraderDetailResponse | null => {
  if (!response) {
    return null
  }

  if (hasWrappedTraderDetailData(response)) {
    return response.data ?? null
  }

  return response
}

const normalizeHeaderLocale = (languageCode?: string) => {
  if (!languageCode) {
    return ''
  }

  return languageCode.toLowerCase() === 'zh-cn' ? 'zh-CN' : languageCode
}

const normalizeWebTraderDetailResponse = (detail: TraderDetailResponse): TraderDetailResponse => {
  if (!detail.basicInfo) {
    return detail
  }

  const basicInfo = detail.basicInfo
  const traderData = detail.traderData ?? {}

  return {
    ...detail,
    traderData: {
      ...traderData,
      basicData: {
        traderCode: basicInfo.traderCode,
        showName: basicInfo.showName,
        shortNameLocal: basicInfo.shortNameLocal || basicInfo.shortName,
        annotation: basicInfo.annotation,
        webUrl: basicInfo.website,
        mobileUrl: basicInfo.mobileUrl,
        ...traderData.basicData,
      },
    },
    companyInfo: detail.companyInfo ?? {
      basicData: basicInfo.displayItems,
    },
  }
}

const normalizeLicenseTabItems = (detail: TraderDetailResponse): TraderDetailResponse => {
  const license = detail.license

  if (!license || !Array.isArray(license.tabItems)) {
    return detail
  }

  return {
    ...detail,
    license: {
      ...license,
      tabItems: license.tabItems.map((item) => ({
        ...item,
        circleFlag: item.circleFlag,
      })),
    },
  }
}

export const getTraderDetail = async ({
  traderCode,
  languageCode,
  countryCode,
  timeRangeType,
}: Readonly<TraderDetailParams>) => {
  const api = useEnvApi()
  const normalizedLanguageCode = normalizeHeaderLocale(languageCode)
  const headers: Record<string, string> = {
    preferredlanguagecode: normalizedLanguageCode || 'zh-CN',
    fxintl: 'true',
    pkgname: 'com.wikifx.bit',
    appchannel: 'ziji',
    basicdata1: '999,0,2,999,0,0,0',
    wsc: '2',
  }

  if (normalizedLanguageCode !== '') {
    headers.LanguageCode = normalizedLanguageCode
  }

  if (countryCode !== undefined && countryCode !== '') {
    headers.CountryCode = countryCode
  }

  const requestParams: Record<string, string | number> = {
    traderCode,
    page: 1,
  }

  if (timeRangeType !== undefined && timeRangeType !== '') {
    requestParams.timeRangeType = timeRangeType
  }
  const config = {
    baseURL: api.wikibitBffGo,
    url: '/api/v4/bit/web/trader/detail',
    method: 'GET' as const,
    params: requestParams,
    headers,
  }

  if (import.meta.server) {
    const response = await useServerFetch<TraderDetailRawResponse>({
      ...config,
      query: requestParams,
    })

    const detail = normalizeTraderDetailResponse(response)

    return detail ? normalizeLicenseTabItems(normalizeWebTraderDetailResponse(detail)) : null
  }

  const response = await request<unknown, TraderDetailRawResponse>(config)

  const detail = normalizeTraderDetailResponse(response)

  return detail ? normalizeLicenseTabItems(normalizeWebTraderDetailResponse(detail)) : null
}
